<template> 
  <div id="drag-form" @dragover.prevent @drop.prevent class="form-container">
    <div class="request_input">  
      <input class="request_input_field" type="text" id="drag-form-request-id" v-model="request_id" placeholder="Request ID" readonly/>
    </div>

    <input id="drag-form-input" style="display:none" ref="file" type="file" multiple @change="uploadFile"/>
    
    <!-- Drop zone -->
    <div
      @click="$refs.file.click()"
      @drop="dragFile"
      @dragover.prevent="dragover"
      @dragleave.prevent="dragleave"
      id="drag-field"
      :class="{ 'drag-field-file-hover': isDragOver }"
    >
      <div v-if="!fileItems.length" class="drop-zone-content">
        <div class="upload-icon">📁</div>
        <div>Drag & drop to upload</div>
        <div style="font-weight: bold;text-decoration: underline;">or browse</div>
      </div>
      
      <!-- File cards grid -->
      <div v-if="fileItems.length" class="file-grid">
        <div v-for="(fileItem, index) in fileItems" :key="fileItem.id" class="file-card">
          <div class="file-card-header">
            <div class="file-name" :title="fileItem.file.name">{{ truncateFileName(fileItem.file.name) }}</div>
            <button 
              v-if="fileItem.status !== 'uploading'" 
              @click="deleteHandler($event, index)" 
              class="delete-button"
            >
              ✕
            </button>
          </div>
          
          <div class="info-line">
            <div class="file-size">{{ formatFileSize(fileItem.file.size) }}</div>
            <div class="file-status">
              <div v-if="fileItem.status === 'pending'" class="status-badge pending">Ready</div>
              <div v-else-if="fileItem.status === 'uploading'" class="status-badge uploading">Uploading</div>
              <div v-else-if="fileItem.status === 'completed'" class="status-badge completed">✓ Complete</div>
              <div v-else-if="fileItem.status === 'error'" class="status-badge error">✗ Error</div>
            </div>
          </div>
          
          <!-- Progress bar -->
          <div class="progress-container">
            <div class="progress-bar">
              <div 
                class="progress-fill" 
                :style="{ width: fileItem.progress + '%' }"
                :class="{ 
                  'progress-uploading': fileItem.status === 'uploading',
                  'progress-completed': fileItem.status === 'completed',
                  'progress-error': fileItem.status === 'error'
                }"
              ></div>
            </div>
            <div class="progress-text">{{ fileItem.progress }}%</div>
          </div>
        </div>
        
        <!-- Add more files area -->
        <div class="add-more-card" @click="$refs.file.click()">
          <div class="add-more-icon">+</div>
          <div>Add more files</div>
        </div>
      </div>
    </div>

    <button 
      class="google-button upload-button" 
      @click="clickHandler"
      :disabled="!fileItems.length || isUploading"
      :class="{ 'button-disabled': !fileItems.length || isUploading }"
    >
      {{ isUploading ? 'Uploading...' : 'Upload Files' }}
    </button>
  </div>
</template>

<script>
const baseURL = process.env.VUE_APP_API_BASE;
import axios from 'axios';

export default {
  name: 'DragAndDropForm',
  data: () => ({
    fileItems: [], // Array of { id, file, progress, status }
    request_id: (new URL(window.location.href)).searchParams.get("request_id"),
    isDragOver: false,
    isUploading: false,
    nextId: 1
  }),
  
  computed: {
    File() {
      // For backward compatibility
      return this.fileItems.map(item => item.file);
    }
  },
  
  methods: {
    uploadFile(e) {
      this.addFiles(e.target.files);
    },
    
    dragFile(e) {
      this.isDragOver = false;
      this.addFiles(e.dataTransfer.files);
    },
    
    dragover() {
      this.isDragOver = true;
    },
    
    dragleave() {
      this.isDragOver = false;
    },
    
    addFiles(files) {
      for (let file of files) {
        this.fileItems.push({
          id: this.nextId++,
          file: file,
          progress: 0,
          status: 'pending' // pending, uploading, completed, error
        });
      }
    },
    
    formatFileSize(bytes) {
      if (bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    },
    
    truncateFileName(name) {
      const viewportWidth = window.innerWidth;
      // Calculate max length based on viewport width
      // Divide by 20 to get rough character count (assuming average char width)
      // Set minimum of 15 chars and maximum of 60 chars
      const maxLength = Math.min(Math.max(Math.floor(viewportWidth / 20), 15), 80);
      
      if (name.length <= maxLength) return name;
      
      const extension = name.split('.').pop();
      const nameWithoutExt = name.substring(0, name.lastIndexOf('.'));
      const truncatedLength = maxLength - extension.length - 3; // 3 for '...'
      
      return nameWithoutExt.substring(0, truncatedLength) + '..' + extension;
    },
    
    async clickHandler() {
      if (this.isUploading || !this.fileItems.length) return;
      
      this.isUploading = true;
      const uploadPromises = [];
      
      for (let i = 0; i < this.fileItems.length; i++) {
        const fileItem = this.fileItems[i];
        if (fileItem.status === 'completed') continue;
        
        uploadPromises.push(this.uploadSingleFile(fileItem));
      }
      
      try {
        await Promise.all(uploadPromises);
        
        // Send finish upload request
        const uploadedFiles = this.fileItems
          .filter(item => item.status === 'completed')
          .map(item => item.file.name);
        
        if (uploadedFiles.length > 0) {
          await axios.post(`${baseURL}/finish_upload?request_id=` + this.request_id, {
            'files': uploadedFiles
          });
          
          this.$toast.show("All selected files were uploaded successfully.", {
            type: "success",
            dismissible: true,
            duration: 10000,
          });
          
          // Reset form  <-- uncomment to reset form after all files are uploaded.
          // this.request_id = "";
          // this.fileItems = [];  don't remove all items
        }
      } catch (error) {
        console.error('Upload process failed:', error);
      } finally {
        this.isUploading = false;
      }
    },
    
    async uploadSingleFile(fileItem) {
      fileItem.status = 'uploading';
      fileItem.progress = 0;
      
      try {
        const response = await axios.post(
          `${baseURL}/upload?request_id=` + this.request_id,
          { 'file': fileItem.file },
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (progressEvent) => {
              const progress = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100));
              fileItem.progress = progress;            
            }
          }
        );
        
        fileItem.status = 'completed';
        fileItem.progress = 100;
        
        return response;
      } catch (error) {
        fileItem.status = 'error';
        fileItem.progress = 0;
        
        const detail = error?.response?.data?.message
                    || error?.response?.data
                    || error?.message
                    || 'Network error';
        
        this.$toast.show(`Upload failed for "${fileItem.file.name}": ${detail}`, {
          type: 'error',
          dismissible: true,
          duration: false
        });
        
        throw error;
      }
    },
    
    deleteHandler(e, index) {
      e.stopPropagation();
      if (this.fileItems[index].status === 'uploading') return;
      this.fileItems.splice(index, 1);
    }
  }
}
</script>

<style scoped>
#drag-form-request-id {
  padding: 5px;
  margin-bottom: 10px;
  border-radius: 5px;
  border: 1px solid #000;
}

.form-container {
  width: 50vw; 
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px;
  box-sizing: border-box;
}

#drag-field {
  width: 100%;  /* Make it fill container width */
  box-sizing: border-box;  /* Include padding in width calculation */
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  background-color: #fff;
  border: 3px dashed #25609c;
  border-radius: 10px;
  padding: 20px;
  min-height: 400px;
}

#drag-field:hover,
.drag-field-file-hover {
  border: 3px solid #25609c;
  background-color: #deefff !important;
}

.drop-zone-content {
  text-align: center;
  color: #666;
}

.upload-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.file-grid {
  display: flex;          /* Change from grid to flex */
  flex-direction: column; /* Stack items vertically */
  gap: 12px;             /* Adjust gap between cards */
  width: 100%;
}

.file-card {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 16px;
  background: white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  position: relative;
  width: 100%;           /* Make cards full width */
  box-sizing: border-box;
  display: flex;         /* For better internal layout */
  flex-direction: column;
}

.file-card:hover {
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  transform: translateY(-2px);
}

.file-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.file-name {
  font-weight: 600;
  font-size: 14px;
  color: #333;
  line-height: 1.3;
  flex: 1;
  margin-right: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.delete-button {
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  font-size: 18px;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.delete-button:hover {
  background-color: #ffebee;
  color: #f44336;
}

.info-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.file-size {
  font-size: 12px;
  color: #666;
  margin-right: auto; /* Push the badge to the right */
}

.file-status {
  margin-right: 0; /* Remove the auto margin */
}

.status-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  margin-right: 0; /* Remove right margin since it's now on the right */
}

.status-badge.pending {
  background-color: #e3f2fd;
  color: #1976d2;
}

.status-badge.uploading {
  background-color: #fff3e0;
  color: #f57c00;
}

.status-badge.completed {
  background-color: #e8f5e8;
  color: #2e7d32;
}

.status-badge.error {
  background-color: #ffebee;
  color: #d32f2f;
}

.progress-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.progress-bar {
  flex: 1;
  height: 6px;
  background-color: #f0f0f0;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  transition: width 0.3s ease;
  border-radius: 3px;
}

.progress-uploading {
  background-color: #ff9800;
}

.progress-completed {
  background-color: #4caf50;
}

.progress-error {
  background-color: #f44336;
}

.progress-text {
  font-size: 11px;
  font-weight: 600;
  color: #666;
  min-width: 32px;
}

.add-more-card {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 60px;     /* Reduce height to match file cards better */
  box-sizing: border-box;
  margin-top: 8px;      /* Add some spacing from the file cards */
  border: 2px dashed #25609c;
  border-radius: 8px;
  padding: 12px;        /* Reduce padding to match file cards */
  background: #f8f9ff;
  color: #25609c;
  cursor: pointer;
  transition: all 0.3s ease;
}

/* Optional: make the icon smaller to match the new height */
.add-more-icon {
  font-size: 24px;      /* Reduce from 32px */
  margin-right: 8px;    /* Add specific margin */
  font-weight: bold;
}

.google-button {
  align-items: center;
  border: none;
  margin-left: auto;  
  margin-right: auto; 
  display: block;     
  justify-content: center;
  outline: none;
  position: relative;
  z-index: 0;
  background: #25609c;
  color: white;
  border-radius: 6px;
  cursor: pointer;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  margin-top: 20px;
  transition: all 0.3s ease;
  width: 100%;
}

.google-button:hover:not(.button-disabled) {
  background: #1976d2;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(37, 96, 156, 0.3);
}

.google-button.button-disabled {
  background: #ccc;
  color: #666;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.request_input {
  width: 100%;  /* Make it fill container width */
  box-sizing: border-box;
  text-align: center;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 20px;
}

.request_input_field {
  width: 100%;
  box-sizing: border-box;
  text-align: center;
  font-size: 16px;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
}

@media (max-width: 768px) {
  .form-container {
    width: 95vw;
    padding: 10px;
  }
  
  #drag-field {
    padding: 15px;
  }
}
</style>